/**
 * Colors and formatters for the /browse listing dialog. Flat tints only; the
 * values come from the "Decide fast" design so every part of the dialog
 * draws from one palette.
 */
export const LD = {
  primary: '#279AF1',
  primaryDark: '#1d84d4',
  navy: '#2B4570',
  navyTint: '#eef2f8',
  green: '#27ae60',
  greenTint: '#eaf7ef',
  greenLine: '#c9ebd6',
  ink: '#111827',
  ink2: '#374151',
  muted: '#6b7280',
  muted2: '#9ca3af',
  line: '#e5e7eb',
  saveBorder: '#d1d5db',
  factDot: '#cbd5e1',
  subtle: '#f9fafb',
  overlay: 'rgba(17, 24, 39, 0.55)',
  pill: 'rgba(17, 24, 39, 0.68)',
  pillLight: 'rgba(255, 255, 255, 0.94)',
} as const;

/** Footnotes under the tour button, with and without a buying-power figure. */
export const AGENT_FEE_FOOTNOTE =
  'Agent from $5,000, paid by the seller · No commitment until you make an offer';
export const NO_COMMITMENT_FOOTNOTE = 'No commitment until you make an offer';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatCurrency = (amount: number): string =>
  currency.format(amount);

/** "$472K", for compact ranges. */
export const formatThousands = (amount: number): string =>
  `$${Math.round(amount / 1000).toLocaleString()}K`;

/** Acreage as the MLS gives it: whole numbers bare, otherwise two places. */
export const formatAcres = (acres: number): string =>
  acres % 1 === 0 ? String(acres) : acres.toFixed(2);

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  SFR: 'Single family',
  CONDO: 'Condo',
  TOWNHOME: 'Townhome',
};

export const propertyTypeLabel = (type?: string | null): string | undefined =>
  (type && PROPERTY_TYPE_LABELS[type]) || undefined;

export interface ScoreTheme {
  label: string;
  /** 1 (almost no room) to 5 (lots of room): which of the five bands. */
  level: 1 | 2 | 3 | 4 | 5;
  fill: string;
  tint: string;
  ink: string;
}

/** How many bands there are; the meter draws one segment per band. */
export const BARGAINING_LEVELS = 5;

/**
 * The score at or above which each verdict applies. These are the API's own
 * tiers (`generateBuyerAnalysis` in market-data.class.ts: Strong 75, Good 60,
 * Some 45, Limited 30, else Competitive) so the dialog never names a position
 * the analysis did not reach. Exported for the test that pins them.
 */
export const BARGAINING_BANDS = {
  strong: 75,
  room: 60,
  some: 45,
  little: 30,
} as const;

/**
 * The bargaining-power verdict, one band per API tier, on a single scale a
 * buyer can order without the number: how much room there is to negotiate.
 */
export const bargainingTheme = (score: number): ScoreTheme => {
  if (score >= BARGAINING_BANDS.strong) {
    return {
      label: 'Lots of room to negotiate',
      level: 5,
      fill: LD.green,
      tint: LD.greenTint,
      ink: '#1e7a45',
    };
  }
  if (score >= BARGAINING_BANDS.room) {
    return {
      label: 'Room to negotiate',
      level: 4,
      fill: LD.primary,
      tint: '#e8f4fe',
      ink: '#1d6fb0',
    };
  }
  if (score >= BARGAINING_BANDS.some) {
    return {
      label: 'Some room to negotiate',
      level: 3,
      fill: '#f59e0b',
      tint: '#fff7e0',
      ink: '#9a6b00',
    };
  }
  if (score >= BARGAINING_BANDS.little) {
    return {
      label: 'Little room to negotiate',
      level: 2,
      fill: '#f97316',
      tint: '#fff1e6',
      ink: '#9a3412',
    };
  }
  return {
    label: 'Almost no room to negotiate',
    level: 1,
    fill: '#f43f5e',
    tint: '#ffe4e6',
    ink: '#be123c',
  };
};
